import React, { Component } from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { connect } from 'react-redux';
import {
  fetchGroupList,
  fetchFacilityList
} from 'shared/actions/actions';
import DashboardService from '../../services/dashboard.service';
import DashboardCard from '../../components/DashboardCard';
import DashboardTable from '../../components/DashboardTable';
import SkeletonLoader from '../../components/SkeletonLoader';
import { CompletionTrendChart, StatusMixChart } from '../../components/DashboardCharts';
import './dashboard_overview.css';

class DashboardOverview extends Component {
  constructor(props) {
    super(props);
    
    // Calculate dynamic year options (current year + next 5 years)
    var currentYear = new Date().getFullYear();
    var yearOptions = [];
    for (var i = 0; i < 6; i++) {
      yearOptions.push(currentYear + i);
    }
    
    this.state = {
      // Filter state
      selectedGroup: '',
      selectedFacility: '',
      selectedYear: currentYear.toString(),
      yearOptions: yearOptions,
      
      // Data state with consistent shape
      loading: false,
      error: null,
      
      // Active tab for Due Today / Next 7 Days
      activeTaskTab: 'today', // 'today' or 'next7days'
      
      // Dashboard data
      todayNext7Days: {
        loading: false,
        data: {
          dueToday: {
            totalDueToday: 0,
            scheduledToday: 0,
            assignedToday: 0,
            overdueToday: 0,
            tasks: []
          },
          next7Days: {
            totalUpcoming: 0,
            tasks: []
          }
        },
        error: null
      },
      
      completionTrend: {
        loading: false,
        data: [],
        error: null
      },
      
      statusMix: {
        loading: false,
        data: {
          scheduled: 0,
          assigned: 0,
          inProgress: 0,
          completed: 0,
          cancelled: 0,
          overdue: 0
        },
        error: null
      },
      
      frequencyDistribution: {
        loading: false,
        data: {
          daily: 0,
          weekly: 0,
          monthly: 0,
          quarterly: 0,
          halfYearly: 0,
          yearly: 0,
          details: []
        },
        error: null
      },
      
      topOverdueFacilities: {
        loading: false,
        data: [],
        error: null
      },
      
      // Height synchronization
      syncedCardHeight: null
    };

    // Create refs for height synchronization (React 16.2 compatible)
    this.attentionCardRef = null;
    this.dueTodayCardRef = null;
    this.dueTodayCardContainerRef = null;
    this.resizeObserver = null;

    // Bind methods
    this.handleGroupChange = this.handleGroupChange.bind(this);
    this.handleFacilityChange = this.handleFacilityChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
    this.loadDashboardData = this.loadDashboardData.bind(this);
    this.switchTaskTab = this.switchTaskTab.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    
    // Debounce timer
    this.debounceTimer = null;
  }

  componentDidMount() {
    // Fetch groups on component mount
    var dispatch = this.props.dispatch;
    dispatch(fetchGroupList());
    
    // Load initial dashboard data with default "All Group" selection (org_id = 0)
    this.loadDashboardData();
    
    // Set up ResizeObserver for Attention Required height synchronization
    this.setupHeightSync();
    
    // Add window resize listener for responsive height adjustments
    window.addEventListener('resize', this.handleWindowResize);
  }
  
  setupHeightSync() {
    var self = this;
    
    // Since we're using CSS grid with fixed heights, we just need to ensure
    // the content areas scroll properly within the fixed card heights
    setTimeout(function() {
      if (self.dueTodayCardContainerRef) {
        var dueTodayContainer = self.dueTodayCardContainerRef;
        var dueTodayCard = dueTodayContainer.querySelector('.dashboard-card');
        if (dueTodayCard) {
          // Ensure the card respects the CSS max-height
          dueTodayCard.style.height = '100%';
          dueTodayCard.style.maxHeight = '500px';
          
          var cardBody = dueTodayCard.querySelector('.dashboard-card-body');
          if (cardBody) {
            // Calculate available height for card body
            var header = dueTodayCard.querySelector('.dashboard-card-header');
            var headerHeight = header ? header.getBoundingClientRect().height : 0;
            var availableHeight = 500 - headerHeight - 32; // 32px for padding
            
            cardBody.style.height = availableHeight + 'px';
            cardBody.style.maxHeight = availableHeight + 'px';
            // Don't set overflow on card body - let task-table-container handle table scrolling
            cardBody.style.overflowY = 'visible';
            cardBody.style.overflowX = 'hidden';
          }
        }
      }
      
      // Also ensure attention card respects the height
      if (self.attentionCardRef) {
        var attentionCard = self.attentionCardRef;
        var card = attentionCard.querySelector('.dashboard-card');
        if (card) {
          card.style.height = '100%';
          card.style.maxHeight = '500px';
          
          var cardBody = card.querySelector('.dashboard-card-body');
          if (cardBody) {
            var header = card.querySelector('.dashboard-card-header');
            var headerHeight = header ? header.getBoundingClientRect().height : 0;
            var availableHeight = 500 - headerHeight - 32;
            
            cardBody.style.height = availableHeight + 'px';
            cardBody.style.maxHeight = availableHeight + 'px';
            // Allow scrolling in the attention card body
            cardBody.style.overflowY = 'auto';
            cardBody.style.overflowX = 'hidden';
          }
        }
      }
    }, 100);
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-sync heights when data changes that might affect card content height
    var dataChanged = (
      prevState.todayNext7Days !== this.state.todayNext7Days ||
      prevState.statusMix !== this.state.statusMix ||
      prevState.activeTaskTab !== this.state.activeTaskTab
    );
    
    if (dataChanged) {
      // Delay height sync to allow DOM to update
      setTimeout(function() {
        if (this.dueTodayCardContainerRef) {
          var dueTodayContainer = this.dueTodayCardContainerRef;
          var dueTodayCard = dueTodayContainer.querySelector('.dashboard-card');
          if (dueTodayCard) {
            dueTodayCard.style.height = '100%';
            dueTodayCard.style.maxHeight = '500px';
            
            var cardBody = dueTodayCard.querySelector('.dashboard-card-body');
            if (cardBody) {
              var header = dueTodayCard.querySelector('.dashboard-card-header');
              var headerHeight = header ? header.getBoundingClientRect().height : 0;
              var availableHeight = 500 - headerHeight - 32;
              
              cardBody.style.height = availableHeight + 'px';
              cardBody.style.maxHeight = availableHeight + 'px';
              // Allow scrolling in the card body - DO NOT set overflow to hidden
              cardBody.style.overflowY = 'auto';
              cardBody.style.overflowX = 'hidden';
            }
          }
        }
      }.bind(this), 50);
    }
  }

  componentWillUnmount() {
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Remove window resize listener
    window.removeEventListener('resize', this.handleWindowResize);
  }

  // Handle window resize for responsive height adjustments
  handleWindowResize() {
    var self = this;
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(function() {
      // Re-apply fixed heights on window resize
      if (self.dueTodayCardContainerRef) {
        var dueTodayContainer = self.dueTodayCardContainerRef;
        var dueTodayCard = dueTodayContainer.querySelector('.dashboard-card');
        if (dueTodayCard) {
          dueTodayCard.style.height = '100%';
          dueTodayCard.style.maxHeight = '500px';
          
          var cardBody = dueTodayCard.querySelector('.dashboard-card-body');
          if (cardBody) {
            var header = dueTodayCard.querySelector('.dashboard-card-header');
            var headerHeight = header ? header.getBoundingClientRect().height : 0;
            var availableHeight = 500 - headerHeight - 32;
            
            cardBody.style.height = availableHeight + 'px';
            cardBody.style.maxHeight = availableHeight + 'px';
            // Allow scrolling in the card body - DO NOT set overflow to hidden
            cardBody.style.overflowY = 'auto';
            cardBody.style.overflowX = 'hidden';
          }
        }
      }
    }, 150);
  }

  // Build filters object for API calls
  buildFilters() {
    var filters = {};
    
    // Handle org_id: 0 means "All Group", specific ID means filtered organization
    if (this.state.selectedGroup !== null && this.state.selectedGroup !== undefined && this.state.selectedGroup !== '') {
      var orgId = parseInt(this.state.selectedGroup, 10);
      if (!isNaN(orgId)) {
        filters.org_id = orgId;
      }
    } else {
      // Fallback to "All Group" if no valid selection
      filters.org_id = 0;
    }
    
    // Only add facility filter if a specific facility is selected (not "All Facility")
    if (this.state.selectedFacility && this.state.selectedFacility !== '') {
      var facilityId = parseInt(this.state.selectedFacility, 10);
      if (!isNaN(facilityId)) {
        filters.facility_ids = facilityId;
      }
    }
    
    if (this.state.selectedYear) {
      filters.year = this.state.selectedYear;
    }
    
    return filters;
  }

  // Centralized dashboard data loading with proper error handling
  loadDashboardData() {
    var self = this;
    
    // Set individual loading states for each component
    self.setState({
      loading: true,
      error: null,
      todayNext7Days: {
        ...self.state.todayNext7Days,
        loading: true,
        error: null
      },
      completionTrend: {
        ...self.state.completionTrend,
        loading: true,
        error: null
      },
      statusMix: {
        ...self.state.statusMix,
        loading: true,
        error: null
      },
      frequencyDistribution: {
        ...self.state.frequencyDistribution,
        loading: true,
        error: null
      },
      topOverdueFacilities: {
        ...self.state.topOverdueFacilities,
        loading: true,
        error: null
      }
    });
    
    var filters = self.buildFilters();
    
    var componentLoadStart = performance.now();
    
    DashboardService.loadAllDashboardData(filters)
      .then(function(results) {
        var componentLoadDuration = (performance.now() - componentLoadStart).toFixed(2);
        // Process each result with data safety
        self.setState({
          todayNext7Days: {
            loading: false,
            data: self.processTodayNext7DaysData(results.todayNext7Days),
            error: results.todayNext7Days && results.todayNext7Days.success === false ? results.todayNext7Days.message : null
          },
          completionTrend: {
            loading: false,
            data: self.processCompletionTrendData(results.completionTrend),
            error: results.completionTrend && results.completionTrend.success === false ? results.completionTrend.message : null
          },
          statusMix: {
            loading: false,
            data: self.processStatusMixData(results.statusMix),
            error: results.statusMix && results.statusMix.success === false ? results.statusMix.message : null
          },
          frequencyDistribution: {
            loading: false,
            data: self.processFrequencyDistributionData(results.frequencyDistribution),
            error: results.frequencyDistribution && results.frequencyDistribution.success === false ? results.frequencyDistribution.message : null
          },
          topOverdueFacilities: {
            loading: false,
            data: self.processTopOverdueFacilitiesData(results.topOverdueFacilities),
            error: results.topOverdueFacilities && results.topOverdueFacilities.success === false ? results.topOverdueFacilities.message : null
          },
          loading: false
        });
      })
      .catch(function(error) {
        var componentLoadDuration = (performance.now() - componentLoadStart).toFixed(2);
        self.setState({
          todayNext7Days: {
            ...self.state.todayNext7Days,
            loading: false,
            error: error.message || 'Failed to load today/next 7 days data'
          },
          completionTrend: {
            ...self.state.completionTrend,
            loading: false,
            error: error.message || 'Failed to load completion trend data'
          },
          statusMix: {
            ...self.state.statusMix,
            loading: false,
            error: error.message || 'Failed to load status mix data'
          },
          frequencyDistribution: {
            ...self.state.frequencyDistribution,
            loading: false,
            error: error.message || 'Failed to load frequency distribution data'
          },
          topOverdueFacilities: {
            ...self.state.topOverdueFacilities,
            loading: false,
            error: error.message || 'Failed to load top facilities data'
          },
          loading: false,
          error: error.message || 'Failed to load dashboard data'
        });
      });
  }

  // Data processors with null safety following API contract
  processTodayNext7DaysData(apiResult) {
    if (!apiResult || !apiResult.success || !apiResult.result) {
      return {
        dueToday: { totalDueToday: 0, scheduledToday: 0, assignedToday: 0, overdueToday: 0, tasks: [] },
        next7Days: { totalUpcoming: 0, tasks: [] }
      };
    }

    var result = apiResult.result;
    var dueToday = result.due_today || {};
    var next7Days = result.next_7_days || {};
    var todayTasks = dueToday.tasks || [];
    var upcomingTasks = next7Days.tasks || [];

    return {
      dueToday: {
        totalDueToday: dueToday.summary && dueToday.summary.total_due_today ? dueToday.summary.total_due_today : dueToday.total_due_today || 0,
        scheduledToday: dueToday.summary && dueToday.summary.scheduled_today ? dueToday.summary.scheduled_today : dueToday.scheduled_today || 0,
        assignedToday: dueToday.summary && dueToday.summary.assigned_today ? dueToday.summary.assigned_today : dueToday.assigned_today || 0,
        overdueToday: dueToday.summary && dueToday.summary.overdue_today ? dueToday.summary.overdue_today : dueToday.overdue_today || 0,
        tasks: todayTasks.map(function(task) {
          return {
            id: task && task.id ? task.id : 0,
            planned_start_date_time: task && task.planned_start_date_time ? task.planned_start_date_time : '',
            contract_facility_name: task && task.contract_facility_name ? task.contract_facility_name : '',
            periodical_name: task && task.periodical_name ? task.periodical_name : '',
            status_name: task && task.status_name ? task.status_name : ''
          };
        })
      },
      next7Days: {
        totalUpcoming: next7Days.total_upcoming || 0,
        tasks: upcomingTasks.map(function(task) {
          return {
            id: task && task.id ? task.id : 0,
            planned_start_date_time: task && task.planned_start_date_time ? task.planned_start_date_time : '',
            contract_facility_name: task && task.contract_facility_name ? task.contract_facility_name : '',
            periodical_name: task && task.periodical_name ? task.periodical_name : '',
            status_name: task && task.status_name ? task.status_name : ''
          };
        })
      }
    };
  }

  processCompletionTrendData(apiResult) {
    if (!apiResult || !apiResult.success || !Array.isArray(apiResult.result)) {
      return [];
    }
    
    return apiResult.result.map(function(month) {
      return {
        month: month && month.month ? month.month : 0,
        month_name: month && month.month_name ? month.month_name : '',
        total_scheduled: month && month.total_scheduled ? month.total_scheduled : 0,
        completed: month && month.completed ? month.completed : 0,
        overdue: month && month.overdue ? month.overdue : 0
      };
    });
  }

  processStatusMixData(apiResult) {
    if (!apiResult || !apiResult.success || !apiResult.result) {
      return { scheduled: 0, assigned: 0, inProgress: 0, completed: 0, cancelled: 0, overdue: 0 };
    }

    var result = apiResult.result;
    return {
      scheduled: result.scheduled || 0,
      assigned: result.assigned || 0,
      inProgress: result.in_progress || 0,
      completed: result.completed || 0,
      cancelled: result.cancelled || 0,
      overdue: result.overdue || 0
    };
  }

  processFrequencyDistributionData(apiResult) {
    if (!apiResult || !apiResult.success || !apiResult.result) {
      return { daily: 0, weekly: 0, monthly: 0, quarterly: 0, halfYearly: 0, yearly: 0, details: [] };
    }

    var result = apiResult.result;
    return {
      daily: Math.max(0, result.daily || 0),
      weekly: Math.max(0, result.weekly || 0),
      monthly: Math.max(0, result.monthly || 0),
      quarterly: Math.max(0, result.quarterly || 0),
      halfYearly: Math.max(0, result.half_yearly || 0),
      yearly: Math.max(0, result.yearly || 0),
      details: Array.isArray(result.details) ? result.details.map(function(item) {
        return {
          frequency_name: item && item.frequency_name ? String(item.frequency_name).trim() : '',
          occur_frequency: Math.max(0, item && item.occur_frequency ? item.occur_frequency : 0),
          count: Math.max(0, item && item.count ? item.count : 0)
        };
      }).filter(function(item) {
        return item.frequency_name && item.count > 0;
      }) : []
    };
  }

  processTopOverdueFacilitiesData(apiResult) {
    if (!apiResult || !apiResult.success || !Array.isArray(apiResult.result)) {
      return [];
    }
    
    return apiResult.result.slice(0, 5).map(function(facility) {
      return {
        facility_id: facility && facility.facility_id ? facility.facility_id : 0,
        facility_name: facility && facility.facility_name ? String(facility.facility_name).trim() : '',
        total_overdue: Math.max(0, facility && facility.total_overdue ? facility.total_overdue : 0),
        monthly_breakdown: Array.isArray(facility && facility.monthly_breakdown) ? facility.monthly_breakdown : []
      };
    }).filter(function(facility) {
      return facility.facility_name && facility.total_overdue >= 0;
    });
  }

  // Event Handlers — update state only; data loads on Apply button click
  handleGroupChange(e) {
    var groupId = e.target.value;
    
    // Validate the selected group ID
    if (groupId === null || groupId === undefined) {
      groupId = '0';
    }
    
    this.setState({ 
      selectedGroup: groupId,
      selectedFacility: '' // Reset facility when organization changes
    }, () => {
      // Automatically load data after state update
      this.debouncedLoadData();
    });
    
    // Fetch facilities for selected group (only if not "All Group")
    if (groupId && groupId !== '0' && parseInt(groupId, 10) > 0) {
      this.props.dispatch(fetchFacilityList(parseInt(groupId, 10)));
    }
  }

  handleFacilityChange(e) {
    var facilityId = e.target.value;
    
    // Validate the selected facility ID
    if (facilityId && facilityId !== '' && isNaN(parseInt(facilityId, 10))) {
      facilityId = '';
    }
    
    this.setState({ selectedFacility: facilityId }, () => {
      // Automatically load data after state update
      this.debouncedLoadData();
    });
  }

  handleYearChange(e) {
    var year = e.target.value;
    this.setState({ selectedYear: year }, () => {
      // Automatically load data after state update
      this.debouncedLoadData();
    });
  }


  // Debounced data loading (300ms delay) with loading state management
  debouncedLoadData() {
    var self = this;
    
    if (this.debounceTimer) {
      console.log('[Dashboard Component] Debounce: Clearing previous timer');
      clearTimeout(this.debounceTimer);
    }
    
    // Set loading states immediately for immediate visual feedback
    self.setState({
      todayNext7Days: {
        ...self.state.todayNext7Days,
        loading: true
      },
      completionTrend: {
        ...self.state.completionTrend,
        loading: true
      },
      statusMix: {
        ...self.state.statusMix,
        loading: true
      },
      frequencyDistribution: {
        ...self.state.frequencyDistribution,
        loading: true
      },
      topOverdueFacilities: {
        ...self.state.topOverdueFacilities,
        loading: true
      }
    });
    
    console.log('[Dashboard Component] Debounce: Starting 300ms timer before loading data');
    this.debounceTimer = setTimeout(function() {
      console.log('[Dashboard Component] Debounce: 300ms elapsed, now calling loadDashboardData()');
      self.loadDashboardData();
    }, 300);
  }

  // Switch between Due Today and Next 7 Days tabs
  switchTaskTab(tab) {
    this.setState({ activeTaskTab: tab });
  }

  // Get status badge class for styling
  getStatusBadgeClass(status) {
    if (!status) return 'scheduled';
    
    var statusLower = status.toLowerCase();
    if (statusLower.includes('completed')) return 'completed';
    if (statusLower.includes('overdue')) return 'overdue';
    if (statusLower.includes('in progress')) return 'in-progress';
    if (statusLower.includes('assigned')) return 'assigned';
    if (statusLower.includes('cancelled')) return 'cancelled';
    return 'scheduled';
  }

  // Format date for display
  formatDate(dateString) {
    if (!dateString) return 'No date';
    try {
      var date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  // Helper to get total live tasks
  getTotalLiveTasks() {
    var statusMix = this.state.statusMix && this.state.statusMix.data ? this.state.statusMix.data : {};
    return (statusMix.scheduled || 0) + (statusMix.assigned || 0) + (statusMix.inProgress || 0);
  }

  // Helper to get overdue count
  getOverdueCount() {
    var statusMix = this.state.statusMix && this.state.statusMix.data ? this.state.statusMix.data : {};
    return statusMix.overdue || 0;
  }

  // Helper methods with null safety
  getCompletionPercentage(month) {
    if (!month || !month.total_scheduled || month.total_scheduled === 0) return 0;
    return Math.round((month.completed / month.total_scheduled) * 100);
  }

  // Get appropriate task data based on active tab
  getTaskDataForTab() {
    var activeTab = this.state.activeTaskTab;
    var todayNext7Days = this.state.todayNext7Days;
    var todayData = todayNext7Days && todayNext7Days.data ? todayNext7Days.data : {};
    
    if (activeTab === 'today') {
      // Return today's tasks from the dedicated due_today.tasks array
      return todayData.dueToday && todayData.dueToday.tasks ? todayData.dueToday.tasks : [];
    }
    
    // Return next 7 days tasks
    return todayData.next7Days && todayData.next7Days.tasks ? todayData.next7Days.tasks : [];
  }

  render() {
    var loading = this.state.loading;
    var error = this.state.error;
    var activeTaskTab = this.state.activeTaskTab;
    var todayNext7Days = this.state.todayNext7Days;
    var completionTrend = this.state.completionTrend;
    var statusMix = this.state.statusMix;
    var frequencyDistribution = this.state.frequencyDistribution;
    var topOverdueFacilities = this.state.topOverdueFacilities;
    var selectedGroup = this.state.selectedGroup;
    var selectedFacility = this.state.selectedFacility;
    var selectedYear = this.state.selectedYear;
    
    var group_data = this.props.group_data;
    var facility_data = this.props.facility_data;
    
    // Extract data with null safety
    var todayData = todayNext7Days && todayNext7Days.data ? todayNext7Days.data : {};
    var completionData = completionTrend && completionTrend.data ? completionTrend.data : [];
    var statusData = statusMix && statusMix.data ? statusMix.data : {};
    var frequencyData = frequencyDistribution && frequencyDistribution.data ? frequencyDistribution.data : {};
    var facilitiesData = topOverdueFacilities && topOverdueFacilities.data ? topOverdueFacilities.data : [];
    
    var liveTasks = this.getTotalLiveTasks();
    var overdueCount = this.getOverdueCount();

    // Table columns for task display
    var taskColumns = [
      { key: 'planned_start_date_time', label: 'Date', width: '20%', render: (val) => this.formatDate(val) },
      { key: 'contract_facility_name', label: 'Facility', width: '30%' },
      { key: 'periodical_name', label: 'Periodical', width: '30%' },
      { 
        key: 'status_name', 
        label: 'Status',
        width: '20%',
        render: (val) => (
          <span className={`status-badge ${this.getStatusBadgeClass(val)}`}>
            {val || 'Unknown'}
          </span>
        )
      }
    ];

    return (
      <div className="dashboard-overview">
        {/* Filter Section */}
        <div className="filter-section-container">
          <div className="dob-filter-row">

            <div className="dob-filter-field dob-filter-field--group">
              <label className="filter-label">Group</label>
              <div className="dob-select-wrap">
                <select
                  className="dob-native-select"
                  value={selectedGroup}
                  onChange={this.handleGroupChange}
                >
                  <option value="0">All Group</option>
                  {group_data && group_data.map(function(group) {
                    return (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="dob-filter-field dob-filter-field--facility">
              <label className="filter-label">Facility</label>
              <div className="dob-select-wrap">
                <select
                  className="dob-native-select"
                  value={selectedFacility}
                  onChange={this.handleFacilityChange}
                  disabled={!selectedGroup || selectedGroup === '0'}
                >
                  <option value="">All Facility</option>
                  {facility_data && facility_data.map(function(facility) {
                    return (
                      <option key={facility.id} value={facility.id}>
                        {facility.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="dob-filter-field dob-filter-field--year">
              <label className="filter-label">Month / Year</label>
              <div className="dob-select-wrap">
                <select
                  className="dob-native-select"
                  value={selectedYear}
                  onChange={this.handleYearChange}
                >
                  {this.state.yearOptions.map(function(year) {
                    return (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

          </div>
        </div>

        {/* Global Error */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Top Statistics Strip */}
        <div className="stats-strip">
          <div className="stat-item stat-item--scheduled">
            <div className="stat-item-label">Scheduled</div>
            {statusMix.loading ? (
              <SkeletonLoader type="line" height="28px" width="60px" />
            ) : (
              <div className="stat-item-number">{statusData.scheduled || 0}</div>
            )}
          </div>
          <div className="stat-strip-divider" />
          <div className="stat-item stat-item--assigned">
            <div className="stat-item-label">Assigned</div>
            {statusMix.loading ? (
              <SkeletonLoader type="line" height="28px" width="60px" />
            ) : (
              <div className="stat-item-number">{statusData.assigned || 0}</div>
            )}
          </div>
          <div className="stat-strip-divider" />
          <div className="stat-item stat-item--completed">
            <div className="stat-item-label">Completed</div>
            {statusMix.loading ? (
              <SkeletonLoader type="line" height="28px" width="60px" />
            ) : (
              <div className="stat-item-number">{statusData.completed || 0}</div>
            )}
          </div>
          <div className="stat-strip-divider" />
          <div className="stat-item stat-item--overdue">
            <div className="stat-item-label">Overdue</div>
            {statusMix.loading ? (
              <SkeletonLoader type="line" height="28px" width="60px" />
            ) : (
              <div className="stat-item-number">{statusData.overdue || 0}</div>
            )}
          </div>
        </div>

        {/* Task Cards and Attention */}
        <Row className="main-content-row" style={{ flexWrap: 'nowrap', gap: 'inherit' }}>
          <Col md="8" ref={(ref) => this.dueTodayCardContainerRef = ref}>
            <DashboardCard
              title="Due Today | Next 7 Days"
              subtitle="Immediate work visibility for supervisors"
              loading={todayNext7Days.loading}
              error={todayNext7Days.error}
            >
              {todayNext7Days.loading ? (
                <div>
                  {/* Skeleton for task tabs */}
                  <div className="task-tabs">
                    <SkeletonLoader type="card" className="task-tab-skeleton" />
                    <SkeletonLoader type="card" className="task-tab-skeleton" />
                  </div>
                  {/* Skeleton for task table */}
                  <div className="task-table-container">
                    <SkeletonLoader type="table" count={5} />
                  </div>
                </div>
              ) : (
                <div className="dashboard-content-fade-in">
                  {/* Clickable Card Tabs */}
                  <div className="task-tabs">
                    <div
                      className={`task-tab ${activeTaskTab === 'today' ? 'active' : ''}`}
                      onClick={() => this.switchTaskTab('today')}
                    >
                      <div className="tab-label">Due Today</div>
                      <div className="tab-number">{todayData.dueToday && todayData.dueToday.totalDueToday ? todayData.dueToday.totalDueToday : 0}</div>
                      <div className="tab-detail">
                        {todayData.dueToday && todayData.dueToday.overdueToday ? todayData.dueToday.overdueToday : 0} overdue
                      </div>
                    </div>
                    <div
                      className={`task-tab ${activeTaskTab === 'next7days' ? 'active' : ''}`}
                      onClick={() => this.switchTaskTab('next7days')}
                    >
                      <div className="tab-label">Next 7 Days</div>
                      <div className="tab-number">{todayData.next7Days && todayData.next7Days.totalUpcoming ? todayData.next7Days.totalUpcoming : 0}</div>
                      <div className="tab-detail">
                        Upcoming tasks
                      </div>
                    </div>
                  </div>

                  {/* Task Table */}
                  <div className="task-table-container">
                    <DashboardTable
                      columns={taskColumns}
                      data={this.getTaskDataForTab()}
                      emptyMessage={activeTaskTab === 'today' ? 'No tasks due today' : 'No tasks in the next 7 days'}
                    />
                  </div>
                </div>
              )}
            </DashboardCard>
          </Col>

          <Col md="4">
            <div ref={(ref) => this.attentionCardRef = ref} style={{ height: '100%' }}>
              <DashboardCard
                title="Attention Required"
                subtitle="Critical items needing action"
              >
                {statusMix.loading ? (
                  <div className="attention-list">
                    <SkeletonLoader type="line" height="60px" className="attention-skeleton" />
                    <SkeletonLoader type="line" height="60px" className="attention-skeleton" />
                    <SkeletonLoader type="line" height="60px" className="attention-skeleton" />
                    <SkeletonLoader type="line" height="60px" className="attention-skeleton" />
                  </div>
                ) : (
                  <div className="attention-list dashboard-content-fade-in">
                    <div className={`attention-item ${overdueCount > 0 ? 'has-error' : ''}`}>
                      <div className="attention-indicator"></div>
                      <div className="attention-content">
                        <div className="attention-main">{overdueCount} overdue periodicals</div>
                        <div className="attention-detail">Require immediate action</div>
                      </div>
                    </div>

                    <div className={`attention-item ${statusData.scheduled > 0 ? 'has-warning' : ''}`}>
                      <div className="attention-indicator"></div>
                      <div className="attention-content">
                        <div className="attention-main">{statusData.scheduled || 0} unassigned scheduled tasks</div>
                        <div className="attention-detail">Require allocation before next cycle</div>
                      </div>
                    </div>

                    <div className={`attention-item ${statusData.inProgress > 0 ? 'has-info' : ''}`}>
                      <div className="attention-indicator"></div>
                      <div className="attention-content">
                        <div className="attention-main">{statusData.inProgress || 0} tasks in progress</div>
                        <div className="attention-detail">Monitor for timely completion</div>
                      </div>
                    </div>

                    <div className={`attention-item ${statusData.cancelled > 0 ? 'has-danger' : ''}`}>
                      <div className="attention-indicator"></div>
                      <div className="attention-content">
                        <div className="attention-main">{statusData.cancelled || 0} cancelled tasks</div>
                        <div className="attention-detail">Review and reschedule if needed</div>
                      </div>
                    </div>
                  </div>
                )}
              </DashboardCard>
            </div>
          </Col>
        </Row>

        {/* Charts Row */}
        <Row className="charts-row" style={{ flexWrap: 'nowrap', gap: 'inherit' }}>
          <Col md="8">
            <DashboardCard
              title="Completion Trend"
              subtitle="Monthly scheduled vs completed vs overdue"
              loading={completionTrend.loading}
              error={completionTrend.error}
            >
              {completionTrend.loading ? (
                <SkeletonLoader type="chart" />
              ) : (
                completionData && completionData.length > 0 ? (
                  <div className="dashboard-content-fade-in">
                    <CompletionTrendChart data={completionData} />
                  </div>
                ) : (
                  <div className="chart-empty">No completion trend data available</div>
                )
              )}
            </DashboardCard>
          </Col>

          <Col md="4">
            <DashboardCard
              title="Status Mix"
              subtitle="Distribution of task statuses"
              loading={statusMix.loading}
              error={statusMix.error}
            >
              {statusMix.loading ? (
                <div className="status-mix-skeleton">
                  <SkeletonLoader type="chart" />
                  <div style={{ marginTop: '20px' }}>
                    <SkeletonLoader type="line" height="16px" width="60%" />
                    <SkeletonLoader type="line" height="16px" width="80%" />
                    <SkeletonLoader type="line" height="16px" width="70%" />
                  </div>
                </div>
              ) : (
                <div className="dashboard-content-fade-in">
                  <StatusMixChart data={statusData} />
                </div>
              )}
            </DashboardCard>
          </Col>
        </Row>

        {/* Bottom Row */}
        <Row className="bottom-row" style={{ flexWrap: 'nowrap', gap: 'inherit' }}>
          <Col md="6">
            <DashboardCard
              title="Frequency Distribution"
              subtitle="Work spread across frequency types"
              loading={frequencyDistribution.loading}
              error={frequencyDistribution.error}
            >
              {frequencyDistribution.loading ? (
                <div className="frequency-list">
                  <SkeletonLoader type="line" height="48px" className="frequency-skeleton" />
                  <SkeletonLoader type="line" height="48px" className="frequency-skeleton" />
                  <SkeletonLoader type="line" height="48px" className="frequency-skeleton" />
                  <SkeletonLoader type="line" height="48px" className="frequency-skeleton" />
                  <SkeletonLoader type="line" height="48px" className="frequency-skeleton" />
                </div>
              ) : (
                <div className="frequency-list dashboard-content-fade-in">
                  {/* Use API-driven frequency data from details array */}
                  {frequencyData.details && frequencyData.details.length > 0 ? (
                    frequencyData.details.map(function(item, index) {
                      // Calculate percentage for bar width based on max value in details
                      var maxValue = Math.max.apply(Math, frequencyData.details.map(function(detail) {
                        return detail.count || 0;
                      }));
                      var currentValue = item.count || 0;
                      var widthPercent = maxValue > 0 ? Math.min((currentValue / maxValue) * 100, 100) : 0;
                      
                      return (
                        <div key={index} className="frequency-item">
                          <div className="frequency-name">{item.frequency_name || 'Unknown'}</div>
                          <div className="frequency-count">{currentValue}</div>
                          <div className="frequency-bar">
                            <div 
                              className="frequency-fill" 
                              style={{width: widthPercent + '%'}}
                            ></div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    // Fallback to summary data if details are not available
                    [
                      { name: 'Daily', key: 'daily' },
                      { name: 'Weekly', key: 'weekly' },
                      { name: 'Monthly', key: 'monthly' },
                      { name: 'Quarterly', key: 'quarterly' },
                      { name: 'Half Yearly', key: 'halfYearly' },
                      { name: 'Yearly', key: 'yearly' }
                    ].map(function(item) {
                      // Calculate percentage for bar width based on max value
                      var maxValue = Math.max(
                        frequencyData.daily || 0,
                        frequencyData.weekly || 0,
                        frequencyData.monthly || 0,
                        frequencyData.quarterly || 0,
                        frequencyData.halfYearly || 0,
                        frequencyData.yearly || 0
                      );
                      var currentValue = frequencyData[item.key] || 0;
                      var widthPercent = maxValue > 0 ? Math.min((currentValue / maxValue) * 100, 100) : 0;
                      
                      return (
                        <div key={item.key} className="frequency-item">
                          <div className="frequency-name">{item.name}</div>
                          <div className="frequency-count">{currentValue}</div>
                          <div className="frequency-bar">
                            <div 
                              className="frequency-fill" 
                              style={{width: widthPercent + '%'}}
                            ></div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </DashboardCard>
          </Col>

          <Col md="6">
            <DashboardCard
              title="Top Facilities by Overdue Work"
              subtitle="Top 5 facilities with highest overdue tasks"
              loading={topOverdueFacilities.loading}
              error={topOverdueFacilities.error}
            >
              {topOverdueFacilities.loading ? (
                <div className="facilities-list">
                  <SkeletonLoader type="line" height="56px" className="facility-skeleton" />
                  <SkeletonLoader type="line" height="56px" className="facility-skeleton" />
                  <SkeletonLoader type="line" height="56px" className="facility-skeleton" />
                  <SkeletonLoader type="line" height="56px" className="facility-skeleton" />
                  <SkeletonLoader type="line" height="56px" className="facility-skeleton" />
                </div>
              ) : (
                facilitiesData && facilitiesData.length > 0 ? (
                  <div className="facilities-list dashboard-content-fade-in">
                    {facilitiesData.map(function(facility, index) {
                      // Ensure we have valid data
                      var facilityName = facility.facility_name || 'Unknown Facility';
                      var totalOverdue = facility.total_overdue || 0;
                      var facilityId = facility.facility_id || index;
                      
                      // Calculate bar width based on max overdue value
                      var maxOverdue = Math.max.apply(Math, facilitiesData.map(function(f) { 
                        return f.total_overdue || 0; 
                      }));
                      var widthPercent = maxOverdue > 0 ? Math.min((totalOverdue / maxOverdue) * 100, 100) : 0;
                      
                      return (
                        <div key={facilityId} className="facility-item">
                          <div className="facility-name" title={facilityName}>{facilityName}</div>
                          <div className="facility-count">{totalOverdue}</div>
                          <div className="facility-bar">
                            <div className="facility-fill" style={{width: widthPercent + '%'}}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-data-message">
                    <div className="no-data-text">No overdue facilities data available</div>
                  </div>
                )
              )}
            </DashboardCard>
          </Col>
        </Row>
      </div>
    );
  }
}

// Redux connection
function mapStateToProps(state) {
  var postsBySubreddit = state.postsBySubreddit;
  var group_data = postsBySubreddit.group_items;
  var facility_data = postsBySubreddit.facility_data;

  return {
    group_data: group_data,
    facility_data: facility_data
  };
}

export default connect(mapStateToProps)(DashboardOverview);



// WEBPACK FOOTER //
// ./src/shared/containers/Dashboard/dashboard_overview.js